import { TextOverlay, type TextOverlayOptions } from 'noodle/engine/overlay';

export interface DamageNumberOverlayOptions extends Omit<TextOverlayOptions, 'depthTested'> {
  /** Lifetime in seconds. Default 1.2. */
  lifetime?: number;
  /** Upward pixel drift per second. Default 80 (matches a 1.2 s rise of ~100 px). */
  riseSpeed?: number;
  /** When in the [0..1] lifetime fraction the alpha fade begins. Default 0.5. */
  fadeStart?: number;
}

/**
 * A short-lived floating damage-number text overlay. Rises in screen-space
 * over its lifetime and fades to transparent in the second half. Self-marks
 * `expired` when its time runs out — the engine's OverlayManager reaps it on
 * the next frame, so no manual remove() is needed at the call site.
 *
 * Always renders on top of geometry (depthTested: false) — combat feedback
 * should be visible even when the target is partially obscured.
 *
 * Game-UI building block — lives in playful rather than noodle.
 */
export class DamageNumberOverlay extends TextOverlay {
  public readonly lifetime: number;
  public readonly riseSpeed: number;
  public readonly fadeStart: number;
  private readonly initialOffsetY: number;
  private elapsed = 0;

  constructor(opts: DamageNumberOverlayOptions) {
    super({ ...opts, depthTested: false });
    this.lifetime = opts.lifetime ?? 1.2;
    this.riseSpeed = opts.riseSpeed ?? 80;
    this.fadeStart = opts.fadeStart ?? 0.5;
    this.initialOffsetY = this.screenOffsetY;
  }

  public override update(dt: number): void {
    this.elapsed += dt;
    if (this.elapsed >= this.lifetime) {
      this.expired = true;
      return;
    }
    const t = this.elapsed / this.lifetime;
    // Ease-out rise (sqrt-ish curve): fast at the start, slower at the end.
    const eased = Math.sqrt(t);
    this.screenOffsetY = this.initialOffsetY - this.riseSpeed * this.lifetime * eased;
    const fadeT = t < this.fadeStart ? 0 : (t - this.fadeStart) / (1 - this.fadeStart);
    this.color[3] = 1 - fadeT;
  }
}
